import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, GitMerge, Info, LayoutGrid, Loader2, MoreVertical, Pencil, Plus, QrCode, RefreshCw, Trash2 } from 'lucide-react';

import { TtqAddParticleDialog, clearTtqAddParticleDraft } from '../components/ttq/TtqAddParticleDialog';
import { TtqSessionDetailDialog } from '../components/ttq/TtqSessionDetailDialog';
import { TtqSessionMergeDialog } from '../components/ttq/TtqSessionMergeDialog';
import { SectionCard } from '../components/ttq/SectionCard';
import {
  TtqUnifiedCanvas,
  TtqBottomMenu,
  TtqBottomPanel1qnq,
  TtqBottomPanel1qna,
  TtqBottomPanel1anq,
  TtqBottomPanelCttq,
  useTtqibLayout,
  type Ttq1qnqSegmentItem,
  type TtqBehaviorId,
} from '../features/ttqib';
import { TtqSegmentSelectionPopup } from '../features/ttqib/TtqSegmentSelectionPopup';
import { AiChatDialogPanel } from '../components/AiChatDialogPanel';
import { buildAiChatSourceParticles } from '../utils/aiChatSourceParticles';
import type { AiChatSegmentAnchor } from '../utils/aiChatSegmentKey';
import { listAiChatAnchorsBatch } from '../services/userAiChatService';
import { fetchWorkbenchParticles } from '../features/ttqib/ttqWorkbenchParticleCache';
import { seedIpttqRecordsFromSession } from '../services/ipttqRecordCache';
import { TTQ_SESSION_OVERLAY_TOP_CLASS, TTQIB_BIRDS_EYE_ZOOM_THRESHOLD, normalizeViewport, ttqZoomForDeriveMode } from '../features/ttqib/ttqibViewport';
import { useTtqCanvasLodMode } from '../features/ttqib/useTtqCanvasLodMode';
import { ttqCanvasGesture } from '../features/ttqib/ttqCanvasGesture';
import { Ttq1qnqReadyDialog } from '../features/ttqib/Ttq1qnqReadyDialog';
import { TtqMultiSolutionAssessModal } from '../features/ttqib/TtqMultiSolutionAssessModal';
import { TtqRegenerateConfirmDialog } from '../features/ttqib/TtqRegenerateConfirmDialog';
import { TtqIpttqToolbarButton } from '../features/ttqib/TtqIpttqToolbarButton';
import { TtqParticleLadderPracticeModal } from '../components/ttq/TtqParticleLadderPracticeModal';
import {
  RevisionPrintPreviewModal,
  useRevisionPrintAction,
} from '../features/review/print';
import { buildTtqPrintParticles } from '../utils/ttqPrintParticles';
import type { TtqQuestionChainVariantId } from '../features/ttqib/ttqBehaviorCatalog';
import { ttqWorkbenchService } from '../features/ttqib/ttqWorkbenchService';
import { formatTtqWorkbenchError } from '../features/ttqib/ttqWorkbenchErrors';
import {
  listTastedAnchorTexts,
  isMultiSolutionDerivedParticle,
  isSessionDerivedParticle,
  listSessionDescendantParticleIds,
  ORIGINAL_PARTICLE_HAS_DESCENDANTS_TIP,
  resolveDerivedProvenance,
  resolveFocusFromSegment,
} from '../features/ttqib/ttqibDerivedStack';
import { sessionParticleIdKey } from '../features/ttqib/ipttqScopeDrift';
import { listTtqBirdsEyeConvergeMemberIds } from '../features/ttqib/ttqibBirdsEyeLayout';
import {
  clearTtqSessionUiState,
  consumeTtqMenuRestore,
  consumeTtqReturnFromKnowledge,
} from '../features/ttqib/ttqSessionUiState';
import { invalidateWorkbenchParticleCache } from '../features/ttqib/ttqWorkbenchParticleCache';
import { invalidateParticleSourceImageFetch } from '../features/ttqib/useParticleEmbeddedImage';
import {
  canHardDeleteTtqSession,
  ttqService,
  type TtqSession,
} from '../services/ttqService';
import { candidateNeedsDiagram } from '../utils/ttqDiagramInference';
import {
  formatTtqSessionProgress,
  needsResolvedSessionTitle,
  resolveTtqParticleTitle,
  resolveTtqSessionTitle,
  ttqSessionNextStepHint,
} from '../utils/ttqDisplay';
import type {
  AssessTtqMultiSolutionWorkbenchResponse,
  TtqModelInvocationOptions,
} from '@kelilearning/psycheflow-contracts';

function pickDefault1qnaCandidateIndexes(assessment: AssessTtqMultiSolutionWorkbenchResponse): number[] {
  return assessment.candidates
    .filter((item) => !item.alreadyGenerated && !item.isSourceParticleMethod)
    .map((item) => item.index);
}

export function TtqSessionPage() {
  const { ttqaId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<TtqSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [behaviorSelectedParticleIds, setBehaviorSelectedParticleIds] = useState<string[]>([]);
  const [bottomMenuOpen, setBottomMenuOpen] = useState(false);
  const [segmentTray, setSegmentTray] = useState<Ttq1qnqSegmentItem[]>([]);
  const [segmentPopupOpen, setSegmentPopupOpen] = useState(false);
  const [segmentAnchorObjectId, setSegmentAnchorObjectId] = useState<string | null>(null);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiChatThreadId, setAiChatThreadId] = useState<string | null>(null);
  const [chatAnchorsByParticleId, setChatAnchorsByParticleId] = useState<
    Record<string, AiChatSegmentAnchor[]>
  >({});
  const [panel1qnqOpen, setPanel1qnqOpen] = useState(false);
  const [panel1qnqSourceId, setPanel1qnqSourceId] = useState<string | null>(null);
  const [panel1qnqSubmitting, setPanel1qnqSubmitting] = useState(false);
  const [panel1qnqError, setPanel1qnqError] = useState<string | null>(null);
  const [panel1qnqErrorDetail, setPanel1qnqErrorDetail] = useState<string | null>(null);
  const [panel1qnaOpen, setPanel1qnaOpen] = useState(false);
  const [panel1qnaSourceId, setPanel1qnaSourceId] = useState<string | null>(null);
  const [panel1qnaSubmitting, setPanel1qnaSubmitting] = useState(false);
  const [panel1qnaError, setPanel1qnaError] = useState<string | null>(null);
  const [panel1qnaErrorDetail, setPanel1qnaErrorDetail] = useState<string | null>(null);
  const [panel1qnaDescription, setPanel1qnaDescription] = useState('');
  const [panel1qnaAssessOpen, setPanel1qnaAssessOpen] = useState(false);
  const [panel1qnaAssessing, setPanel1qnaAssessing] = useState(false);
  const [panel1qnaGenerating, setPanel1qnaGenerating] = useState(false);
  const [panel1qnaAssessment, setPanel1qnaAssessment] = useState<AssessTtqMultiSolutionWorkbenchResponse | null>(null);
  const [panel1qnaAssessError, setPanel1qnaAssessError] = useState<string | null>(null);
  const [panel1qnaAssessErrorDetail, setPanel1qnaAssessErrorDetail] = useState<string | null>(null);
  const [panel1qnaSelectedIndexes, setPanel1qnaSelectedIndexes] = useState<number[]>([]);
  const [panel1qnaImageModel, setPanel1qnaImageModel] = useState('wan2.7-image');
  const [panel1qnaImageModels, setPanel1qnaImageModels] = useState<Array<{ id: string; label: string }>>([
    { id: 'wan2.7-image-pro', label: '万相 2.7 Pro' },
    { id: 'wan2.7-image', label: '万相 2.7（推荐）' },
    { id: 'z-image-turbo', label: 'Z-Image Turbo（快）' },
    { id: 'qwen-image-2.0-pro', label: 'Qwen Image 2.0 Pro' },
    { id: 'qwen-image-3.0-pro', label: 'Qwen Image 3.0 Pro' },
  ]);
  const [panel1qnaLlmModel, setPanel1qnaLlmModel] = useState<TtqModelInvocationOptions | undefined>(undefined);
  const [panel1anqOpen, setPanel1anqOpen] = useState(false);
  const [panel1anqMemberIds, setPanel1anqMemberIds] = useState<string[]>([]);
  const [panel1anqSubmitting, setPanel1anqSubmitting] = useState(false);
  const [panel1anqError, setPanel1anqError] = useState<string | null>(null);
  const [panelCttqOpen, setPanelCttqOpen] = useState(false);
  const [panelCttqMemberIds, setPanelCttqMemberIds] = useState<string[]>([]);
  const [panelCttqSubmitting, setPanelCttqSubmitting] = useState(false);
  const [panelCttqError, setPanelCttqError] = useState<string | null>(null);
  const [panelCttqErrorDetail, setPanelCttqErrorDetail] = useState<string | null>(null);
  const [birdsSelectedIds, setBirdsSelectedIds] = useState<string[]>([]);
  const [readyDialogOpen, setReadyDialogOpen] = useState(false);
  const [pendingCenterParticleId, setPendingCenterParticleId] = useState<string | null>(null);
  /** 倒计时结束后应落在多对一（聚合/收敛）还是一对多（衍生）LOD */
  const [pendingCenterPreferBirdsEye, setPendingCenterPreferBirdsEye] = useState(false);
  const [birdsEyeFocusParticleId, setBirdsEyeFocusParticleId] = useState<string | null>(null);
  const [pendingDeleteDerivedId, setPendingDeleteDerivedId] = useState<string | null>(null);
  const [pendingRegenParticleId, setPendingRegenParticleId] = useState<string | null>(null);
  const [pendingCttqMemberIds, setPendingCttqMemberIds] = useState<string[] | null>(null);
  const [cttqMemberPickTargetId, setCttqMemberPickTargetId] = useState<string | null>(null);
  const [deletingDerived, setDeletingDerived] = useState(false);
  const [regeneratingDerivedId, setRegeneratingDerivedId] = useState<string | null>(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePeerSessions, setMergePeerSessions] = useState<TtqSession[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [ladderPracticeParticleId, setLadderPracticeParticleId] = useState<string | null>(null);
  const [ttqToast, setTtqToast] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reloadingSession, setReloadingSession] = useState(false);
  const [titleRefreshMessage, setTitleRefreshMessage] = useState<string | null>(null);
  const menuRestoredRef = useRef(false);
  const selectionRef = useRef<string[]>([]);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const handledKnowledgeReturnKeyRef = useRef<string | null>(null);
  const pendingKnowledgeReturnParticleIdRef = useRef<string | null>(null);

  // 从知识颗粒详情返回：先清缓存，再在 effect 里静默重载会话，使卡片内容与视口对齐最新数据
  if (handledKnowledgeReturnKeyRef.current !== location.key) {
    handledKnowledgeReturnKeyRef.current = location.key;
    const pendingReturn = consumeTtqReturnFromKnowledge(ttqaId);
    const particleId = pendingReturn?.particleId || null;
    pendingKnowledgeReturnParticleIdRef.current = particleId;
    if (particleId) {
      invalidateWorkbenchParticleCache(particleId);
      invalidateParticleSourceImageFetch(particleId);
    }
  }

  const {
    canvasModel,
    setViewport,
    moveParticle,
    switchLayer,
    selectObject,
    setDerivedFocus,
    centerParticle,
    measureParticle,
    setBirdsEyePinnedFloats,
    relayoutCards,
    convergeRelayoutNonce,
  } = useTtqibLayout(session);

  const birdsEyeMode = useTtqCanvasLodMode(canvasModel?.state.viewport?.zoom);
  const birdsEyeDeepLinkAppliedRef = useRef(false);
  const prevBirdsEyeModeRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (birdsEyeDeepLinkAppliedRef.current) return;
    if (searchParams.get('view') !== 'birds-eye') return;
    birdsEyeDeepLinkAppliedRef.current = true;
    const current = normalizeViewport(canvasModel?.state.viewport);
    setViewport({ ...current, zoom: TTQIB_BIRDS_EYE_ZOOM_THRESHOLD });
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  }, [canvasModel?.state.viewport, searchParams, setSearchParams, setViewport]);

  useEffect(() => {
    if (searchParams.get('addParticle') !== '1') return;
    setAddOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('addParticle');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const sessionParticleIds = useMemo(
    () => (session?.particles ?? []).map((entry) => entry.particleId),
    [session?.particles],
  );
  const sessionParticleIdsKey = useMemo(
    () => [...sessionParticleIds].sort().join(','),
    [sessionParticleIds],
  );

  const applyBehaviorSelection = useCallback((
    updater: string[] | ((prev: string[]) => string[]),
  ) => {
    setBehaviorSelectedParticleIds((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      selectionRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (prevBirdsEyeModeRef.current === null) {
      prevBirdsEyeModeRef.current = birdsEyeMode;
      return;
    }
    if (prevBirdsEyeModeRef.current === birdsEyeMode) return;
    prevBirdsEyeModeRef.current = birdsEyeMode;
    if (birdsEyeMode) {
      setBottomMenuOpen(false);
      setPanel1qnaOpen(false);
      setPanel1qnqOpen(false);
      setSegmentTray([]);
      applyBehaviorSelection([]);
    } else {
      setPanel1anqOpen(false);
      setPanelCttqOpen(false);
      setBirdsSelectedIds([]);
      setCttqMemberPickTargetId(null);
      setPendingCttqMemberIds(null);
    }
  }, [applyBehaviorSelection, birdsEyeMode]);

  useEffect(() => {
    selectionRef.current = behaviorSelectedParticleIds;
  }, [behaviorSelectedParticleIds]);

  useEffect(() => {
    menuRestoredRef.current = false;
  }, [ttqaId]);

  useEffect(() => {
    if (loading || menuRestoredRef.current) return;
    if (!sessionParticleIds.length) return;
    menuRestoredRef.current = true;

    const saved = consumeTtqMenuRestore(ttqaId);
    if (!saved?.selectedParticleIds.length) return;

    const valid = new Set(sessionParticleIds);
    const ids = saved.selectedParticleIds.filter((id) => valid.has(id));
    if (ids.length > 0) {
      applyBehaviorSelection(ids);
      setBottomMenuOpen(saved.bottomMenuOpen !== false);
    }
  }, [applyBehaviorSelection, loading, sessionParticleIds, sessionParticleIdsKey, ttqaId]);

  useEffect(() => {
    if (!sessionParticleIds.length) return;
    applyBehaviorSelection((prev) => {
      if (prev.length === 0) return prev;
      const valid = new Set(sessionParticleIds);
      return prev.filter((id) => valid.has(id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionParticleIdsKey]);

  const handleBehaviorSelectionChange = useCallback((particleIds: string[]) => {
    applyBehaviorSelection(particleIds);
    if (particleIds.length === 0) setBottomMenuOpen(false);
  }, [applyBehaviorSelection]);

  const handleOpenPinTi = useCallback((particleId: string) => {
    applyBehaviorSelection([particleId]);
    setSegmentTray([]);
    setPanel1qnqOpen(false);
    setPanel1qnqSourceId(null);
    setPanel1qnqError(null);
    setPanel1qnqErrorDetail(null);
    setPanel1qnaOpen(false);
    setPanel1anqOpen(false);
    setBottomMenuOpen(true);
  }, [applyBehaviorSelection]);

  const handleOpenLadderPractice = useCallback((particleId: string) => {
    setLadderPracticeParticleId(particleId);
  }, []);

  const ttqPrintParticleIds = useMemo(
    () => (birdsEyeMode ? birdsSelectedIds : behaviorSelectedParticleIds),
    [behaviorSelectedParticleIds, birdsEyeMode, birdsSelectedIds],
  );

  const ttqPrintParticles = useMemo(
    () => (session ? buildTtqPrintParticles(session, ttqPrintParticleIds) : []),
    [session, ttqPrintParticleIds],
  );

  const ttqPrintAction = useRevisionPrintAction({
    particles: ttqPrintParticles,
    sessionId: ttqaId,
    sheetKind: 'ttqas',
    ttqaId,
    onNotice: (message) => setTtqToast(message),
  });

  const openTtqPrintPreview = useCallback(() => {
    ttqPrintAction.openPreview();
  }, [ttqPrintAction]);

  const openTtqScan = useCallback(() => {
    if (!session) return;
    const params = new URLSearchParams({
      bindTtqaId: session.id,
      answerSheetKind: 'ttqas',
      finishMode: 'ttq-session',
    });
    navigate(`/scan?${params.toString()}`);
  }, [navigate, session]);

  const ladderPracticeTitle = useMemo(() => {
    if (!ladderPracticeParticleId || !session?.particles) return null;
    const index = session.particles.findIndex((item) => item.particleId === ladderPracticeParticleId);
    if (index < 0) return null;
    const entry = session.particles[index];
    return resolveTtqParticleTitle(entry, index);
  }, [ladderPracticeParticleId, session?.particles]);

  const showLadderPractice = session?.subjectCode === 'math';

  useEffect(() => {
    const notice = (location.state as { ttqScanNotice?: string | null } | null)?.ttqScanNotice;
    if (!notice) return;
    setTtqToast(notice);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!ttqToast) return undefined;
    const timer = window.setTimeout(() => setTtqToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [ttqToast]);

  const handleClearSegmentTray = useCallback(() => {
    setSegmentTray([]);
    setSegmentPopupOpen(false);
    setSegmentAnchorObjectId(null);
    setAiChatOpen(false);
    setPanel1qnqOpen(false);
    setPanel1qnqSourceId(null);
    setPanel1qnqError(null);
    setPanel1qnqErrorDetail(null);
  }, []);

  const handleClearBehaviorSelection = useCallback(() => {
    applyBehaviorSelection([]);
    setBottomMenuOpen(false);
    clearTtqSessionUiState(ttqaId);
  }, [applyBehaviorSelection, ttqaId]);

  const ensureSessionHasTitle = useCallback(async (options?: { force?: boolean }) => {
    if (!ttqaId) return;
    const force = Boolean(options?.force);
    if (force) {
      setTitleRefreshMessage(null);
    }
    try {
      const result = await ttqService.ensureSessionTitle(ttqaId, { force });
      setSession((current) => {
        if (!current) return result.session;
        if (current.title === result.session.title && !force) return current;
        return { ...current, title: result.session.title };
      });
      if (force) {
        setTitleRefreshMessage(result.message);
        window.setTimeout(() => setTitleRefreshMessage(null), 3200);
      }
    } catch (error) {
      if (force) {
        setTitleRefreshMessage(error instanceof Error ? error.message : '刷新标题失败');
        window.setTimeout(() => setTitleRefreshMessage(null), 4200);
      }
    }
  }, [ttqaId]);

  const enterBirdsEyeZoom = useCallback(() => {
    setBottomMenuOpen(false);
    setPanel1qnaOpen(false);
    setPanel1qnqOpen(false);
    setPanel1anqOpen(false);
    setPanelCttqOpen(false);
    setSegmentTray([]);
    applyBehaviorSelection([]);
    const current = normalizeViewport(canvasModel?.state.viewport);
    setViewport({ ...current, zoom: TTQIB_BIRDS_EYE_ZOOM_THRESHOLD });
  }, [applyBehaviorSelection, canvasModel?.state.viewport, setViewport]);

  const enterDeriveZoom = useCallback(() => {
    setPanel1anqOpen(false);
    setPanelCttqOpen(false);
    setBirdsSelectedIds([]);
    setCttqMemberPickTargetId(null);
    setPendingCttqMemberIds(null);
    const current = normalizeViewport(canvasModel?.state.viewport);
    setViewport({ ...current, zoom: ttqZoomForDeriveMode() });
  }, [canvasModel?.state.viewport, setViewport]);

  const handleSelectBehavior = useCallback((id: TtqBehaviorId) => {
    const ids = birdsEyeMode ? birdsSelectedIds : selectionRef.current;
    const sourceId = ids[0] || null;
    if (!sourceId) return;

    if (id === 'multi_solution') {
      if (ids.length > 1 || birdsEyeMode) return;
      if (session && isMultiSolutionDerivedParticle(session, sourceId)) return;
      setBottomMenuOpen(false);
      setPanel1qnqOpen(false);
      setPanel1anqOpen(false);
      setPanelCttqOpen(false);
      setSegmentTray([]);
      setPanel1qnaSourceId(sourceId);
      setPanel1qnaError(null);
      setPanel1qnaOpen(true);
      void ensureSessionHasTitle();
      return;
    }

    if (id === 'question_chain') {
      if (ids.length > 1 || birdsEyeMode) return;
      setBottomMenuOpen(false);
      setPanel1qnaOpen(false);
      setPanel1anqOpen(false);
      setPanelCttqOpen(false);
      setSegmentTray([]);
      setSegmentPopupOpen(false);
      setSegmentAnchorObjectId(null);
      setAiChatOpen(false);
      setPanel1qnqSourceId(sourceId);
      setPanel1qnqError(null);
      setPanel1qnqErrorDetail(null);
      setPanel1qnqOpen(true);
      void ensureSessionHasTitle();
      return;
    }

    if (id === 'pattern_unify') {
      if (ids.length < 2 || !birdsEyeMode) return;
      setBottomMenuOpen(false);
      setPanel1qnaOpen(false);
      setPanel1qnqOpen(false);
      setPanelCttqOpen(false);
      setSegmentTray([]);
      setPanel1anqMemberIds(ids);
      setPanel1anqError(null);
      setPanel1anqOpen(true);
      void ensureSessionHasTitle();
      return;
    }

    if (id === 'cttq_converge') {
      if (ids.length < 2 || !birdsEyeMode) return;
      setBottomMenuOpen(false);
      setPanel1qnaOpen(false);
      setPanel1qnqOpen(false);
      setPanel1anqOpen(false);
      setSegmentTray([]);
      setPanelCttqMemberIds(ids);
      setPanelCttqError(null);
      setPanelCttqOpen(true);
      void ensureSessionHasTitle();
    }
  }, [birdsEyeMode, birdsSelectedIds, ensureSessionHasTitle, session]);

  const handleToggleSegment = useCallback((item: Ttq1qnqSegmentItem) => {
    if (ttqCanvasGesture.isSuppressingUi() || ttqCanvasGesture.shouldBlockClick()) return;
    setBottomMenuOpen(false);
    setPanel1qnaOpen(false);
    setPanel1anqOpen(false);
    setPanelCttqOpen(false);
    // 选段不再直接打开 Panel，先出近旁 Popup
    setPanel1qnqSourceId(item.particleId);
    setPanel1qnqError(null);
    setPanel1qnqErrorDetail(null);
    setSegmentTray((prev) => {
      const exists = prev.some((entry) => entry.objectId === item.objectId);
      if (exists) {
        const next = prev.filter((entry) => entry.objectId !== item.objectId);
        if (next.length === 0) {
          setSegmentPopupOpen(false);
          setSegmentAnchorObjectId(null);
        } else if (!panel1qnqOpen) {
          setSegmentPopupOpen(true);
          setSegmentAnchorObjectId(item.objectId);
        }
        return next;
      }
      // 一题多题仅允许同一知识颗粒选段；跨颗粒时清空旧 tray，只保留本段
      const foreign = prev.some((entry) => entry.particleId !== item.particleId);
      const next = foreign ? [item] : [...prev, item];
      if (!panel1qnqOpen) {
        setSegmentPopupOpen(true);
        setSegmentAnchorObjectId(item.objectId);
      }
      return next;
    });
    if (session) {
      const focus = resolveFocusFromSegment(session, item.particleId, item.text);
      if (focus) setDerivedFocus(focus);
    }
    void ensureSessionHasTitle();
  }, [ensureSessionHasTitle, panel1qnqOpen, session, setDerivedFocus]);

  const handleOpenSegmentPinTi = useCallback(() => {
    setSegmentPopupOpen(false);
    setPanel1qnqOpen(true);
  }, []);

  const loadSession = useCallback(async (silent = false, options?: { forceRefresh?: boolean }) => {
    if (!ttqaId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await ttqService.getSession(ttqaId, { forceRefresh: options?.forceRefresh === true });
      setSession(data);
      if (Array.isArray(data.ipttqRecords) && data.ipttqRecords.length) {
        seedIpttqRecordsFromSession(data.ipttqRecords);
      }
      // 进入详情（非静默轮询）：无标题且已有颗粒时，主动补写列表标题
      if (
        !silent
        && (data.particles?.length ?? 0) > 0
        && needsResolvedSessionTitle(data.title)
      ) {
        void ensureSessionHasTitle();
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载品题会话失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [ensureSessionHasTitle, ttqaId]);

  const handleReloadSession = useCallback(async () => {
    if (reloadingSession) return;
    setReloadingSession(true);
    try {
      await loadSession(true, { forceRefresh: true });
    } finally {
      setReloadingSession(false);
    }
  }, [loadSession, reloadingSession]);

  useEffect(() => {
    const particleId = pendingKnowledgeReturnParticleIdRef.current;
    if (!particleId || !ttqaId) return;
    pendingKnowledgeReturnParticleIdRef.current = null;
    void loadSession(true);
  }, [loadSession, location.key, ttqaId]);

  const handleSubmit1qnq = useCallback(async (payload: {
    description: string;
    items: Ttq1qnqSegmentItem[];
    questionChainVariantId: TtqQuestionChainVariantId;
    sourceParticleId?: string;
    directionPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => {
    const itemParticleIds = [...new Set(
      payload.items.map((item) => String(item.particleId || '').trim()).filter(Boolean),
    )];
    if (itemParticleIds.length > 1) {
      setPanel1qnqError('一题多题仅支持同一知识颗粒的选段，请清空后重选。');
      setPanel1qnqErrorDetail(null);
      return;
    }
    const particleId = payload.sourceParticleId
      || panel1qnqSourceId
      || itemParticleIds[0]
      || undefined;
    if (!particleId || !ttqaId) return;
    const anchorText = payload.items
      .map((item) => String(item.text || '').trim())
      .filter(Boolean)
      .map((text, index) => `${index + 1}. ${text}`)
      .join('\n');
    const directionPreview = String(payload.directionPreview || '').trim() || undefined;
    setPanel1qnqSubmitting(true);
    setPanel1qnqError(null);
    setPanel1qnqErrorDetail(null);
    try {
      const result = await ttqWorkbenchService.runQuestionChain(session?.id || ttqaId, {
        sourceParticleIds: [particleId],
        questionChainVariantId: payload.questionChainVariantId,
        batchSize: 1,
        anchorText: anchorText || undefined,
        customPrompt: payload.description || undefined,
        directionPreview,
        model: payload.model,
      });
      setDerivedFocus({
        sourceParticleId: particleId,
        pathRunId: result.pathRun?.id,
        questionChainVariantId: payload.questionChainVariantId,
        anchorText: anchorText || undefined,
      });
      const derivedId = result.derivedParticleIds?.[0] || null;
      setPendingCenterParticleId(derivedId);
      setPendingCenterPreferBirdsEye(false);
      if (result.ttqa) {
        setSession(result.ttqa as TtqSession);
      } else {
        void loadSession(true);
      }
      setReadyDialogOpen(true);
      setPanel1qnqOpen(false);
    } catch (submitError) {
      const formatted = formatTtqWorkbenchError(submitError);
      setPanel1qnqError(formatted.summary);
      setPanel1qnqErrorDetail(formatted.detail);
    } finally {
      setPanel1qnqSubmitting(false);
    }
  }, [loadSession, panel1qnqSourceId, session?.id, setDerivedFocus, ttqaId]);

  const handleReadyDialogClose = useCallback(() => {
    setReadyDialogOpen(false);
    setSegmentTray([]);
    setSegmentPopupOpen(false);
    setSegmentAnchorObjectId(null);
    setAiChatOpen(false);
    setPanel1qnqOpen(false);
    setPanel1qnqSourceId(null);
    setPanel1qnqError(null);
    setPanel1qnqErrorDetail(null);
    setPanel1qnaOpen(false);
    setPanel1qnaSourceId(null);
    setPanel1qnaError(null);
    setPanel1qnaErrorDetail(null);
    setPanel1qnaAssessError(null);
    setPanel1qnaAssessErrorDetail(null);
    setPanel1qnaLlmModel(undefined);
    const targetId = pendingCenterParticleId;
    const preferBirdsEye = pendingCenterPreferBirdsEye;
    setPendingCenterParticleId(null);
    setPendingCenterPreferBirdsEye(false);
    if (!targetId) return;
    // 先切到目标颗粒所在 LOD，再挂 focus；画布未含新卡时会重试，成功后再清
    if (preferBirdsEye && !birdsEyeMode) {
      enterBirdsEyeZoom();
    } else if (!preferBirdsEye && birdsEyeMode) {
      enterDeriveZoom();
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setBirdsEyeFocusParticleId(targetId);
      });
    });
  }, [
    birdsEyeMode,
    enterBirdsEyeZoom,
    enterDeriveZoom,
    pendingCenterParticleId,
    pendingCenterPreferBirdsEye,
  ]);

  const handlePreview1qnqDirection = useCallback(async (payload: {
    description: string;
    items: Ttq1qnqSegmentItem[];
    questionChainVariantId: TtqQuestionChainVariantId;
    sourceParticleId?: string;
    model?: TtqModelInvocationOptions;
  }) => {
    const itemParticleIds = [...new Set(
      payload.items.map((item) => String(item.particleId || '').trim()).filter(Boolean),
    )];
    if (itemParticleIds.length > 1) {
      throw new Error('一题多题仅支持同一知识颗粒的选段，请清空后重选。');
    }
    const particleId = payload.sourceParticleId
      || panel1qnqSourceId
      || itemParticleIds[0]
      || undefined;
    if (!particleId || !ttqaId) {
      throw new Error('缺少源颗粒');
    }
    const data = await ttqWorkbenchService.previewQuestionChainDirection(session?.id || ttqaId, {
      sourceParticleIds: [particleId],
      questionChainVariantId: payload.questionChainVariantId,
      batchSize: 1,
      anchorText: payload.items
        .map((item) => String(item.text || '').trim())
        .filter(Boolean)
        .map((text, index) => `${index + 1}. ${text}`)
        .join('\n') || undefined,
      customPrompt: payload.description || undefined,
      model: payload.model,
    });
    return {
      directionPreview: data.directionPreview,
    };
  }, [panel1qnqSourceId, session?.id, ttqaId]);

  const run1qnaAssess = useCallback(async (
    sourceParticleId: string,
    model?: TtqModelInvocationOptions,
  ) => {
    const sessionId = session?.id || ttqaId;
    if (!sessionId || !sourceParticleId) return;
    setPanel1qnaAssessing(true);
    setPanel1qnaAssessError(null);
    setPanel1qnaAssessErrorDetail(null);
    setPanel1qnaAssessment(null);
    setPanel1qnaSelectedIndexes([]);
    try {
      const result = await ttqWorkbenchService.assessMultiSolution(sessionId, {
        sourceParticleIds: [sourceParticleId],
        model,
      });
      setPanel1qnaAssessment(result);
      setPanel1qnaSelectedIndexes(pickDefault1qnaCandidateIndexes(result));
    } catch (assessError) {
      const formatted = formatTtqWorkbenchError(assessError);
      setPanel1qnaAssessError(formatted.summary);
      setPanel1qnaAssessErrorDetail(formatted.detail);
    } finally {
      setPanel1qnaAssessing(false);
    }
  }, [session?.id, ttqaId]);

  const handleAssess1qna = useCallback(async (payload: {
    sourceParticleId: string;
    description: string;
    model?: TtqModelInvocationOptions;
  }) => {
    if (!ttqaId) return;
    setPanel1qnaSubmitting(true);
    setPanel1qnaError(null);
    setPanel1qnaErrorDetail(null);
    setPanel1qnaDescription(payload.description || '');
    setPanel1qnaSourceId(payload.sourceParticleId);
    setPanel1qnaLlmModel(payload.model);
    setPanel1qnaAssessOpen(true);
    try {
      // 预取配图模型列表（失败则保留默认）
      void ttqWorkbenchService.getImageGenerationProviders(session?.id || ttqaId)
        .then((resp) => {
          const models = resp.providers[0]?.models || [];
          if (models.length) setPanel1qnaImageModels(models);
          if (resp.defaultModel) setPanel1qnaImageModel(resp.defaultModel);
        })
        .catch(() => undefined);
      await run1qnaAssess(payload.sourceParticleId, payload.model);
    } finally {
      setPanel1qnaSubmitting(false);
    }
  }, [run1qnaAssess, session?.id, ttqaId]);

  const handleToggle1qnaCandidate = useCallback((index: number) => {
    setPanel1qnaSelectedIndexes((prev) => (
      prev.includes(index) ? prev.filter((value) => value !== index) : [...prev, index]
    ));
  }, []);

  const handleConfirm1qnaGenerate = useCallback(async () => {
    const sourceParticleId = panel1qnaSourceId;
    const sessionId = session?.id || ttqaId;
    if (!sessionId || !sourceParticleId || !panel1qnaAssessment || panel1qnaSelectedIndexes.length === 0) {
      return;
    }

    const selected = panel1qnaAssessment.candidates.filter(
      (item) => panel1qnaSelectedIndexes.includes(item.index)
        && !item.alreadyGenerated
        && !item.isSourceParticleMethod,
    );
    if (selected.length === 0) return;

    setPanel1qnaGenerating(true);
    setPanel1qnaAssessError(null);
    setPanel1qnaAssessErrorDetail(null);
    const generatedIds: string[] = [];
    const errors: string[] = [];
    const errorDetails: string[] = [];
    let latestSession: TtqSession | null = null;

    try {
      for (const candidate of selected) {
        try {
          const needsDiagram = candidateNeedsDiagram(candidate);
          const result = await ttqWorkbenchService.runMultiSolution(sessionId, {
            sourceParticleIds: [sourceParticleId],
            batchSize: 1,
            selectedSolutionIndex: candidate.index,
            approachHint: `${candidate.label}：${candidate.approachSummary}`,
            customPrompt: panel1qnaDescription || undefined,
            requiresDiagram: needsDiagram,
            imageGeneration: needsDiagram
              ? { provider: 'dashscope', model: panel1qnaImageModel, enabled: true }
              : undefined,
            model: panel1qnaLlmModel,
          });
          if (result.ttqa) {
            latestSession = result.ttqa as TtqSession;
          }
          const derivedId = result.derivedParticleIds?.[0];
          if (derivedId) generatedIds.push(derivedId);
        } catch (genError) {
          const formatted = formatTtqWorkbenchError(genError);
          errors.push(`${candidate.label}：${formatted.summary}`);
          errorDetails.push(`【${candidate.label}】\n${formatted.detail}`);
        }
      }

      if (latestSession) {
        setSession(latestSession);
      } else if (generatedIds.length > 0) {
        await loadSession(true);
      }

      if (errors.length > 0) {
        setPanel1qnaAssessError(errors.join('\n'));
        setPanel1qnaAssessErrorDetail(errorDetails.join('\n\n---\n\n'));
      }

      if (generatedIds.length > 0 && errors.length === 0) {
        setDerivedFocus({ sourceParticleId });
        setPendingCenterParticleId(generatedIds[generatedIds.length - 1] || null);
        setPendingCenterPreferBirdsEye(false);
        setPanel1qnaAssessOpen(false);
        setPanel1qnaOpen(false);
        setPanel1qnaAssessment(null);
        setReadyDialogOpen(true);
      }
    } finally {
      setPanel1qnaGenerating(false);
    }
  }, [
    loadSession,
    panel1qnaAssessment,
    panel1qnaDescription,
    panel1qnaImageModel,
    panel1qnaLlmModel,
    panel1qnaSelectedIndexes,
    panel1qnaSourceId,
    session?.id,
    setDerivedFocus,
    ttqaId,
  ]);

  const handleAssess1anq = useCallback(async (payload: {
    memberParticleIds: string[];
    description: string;
    model?: TtqModelInvocationOptions;
  }) => {
    if (!session) throw new Error('会话未加载');
    return ttqWorkbenchService.assessPatternUnify(session.id, {
      sourceParticleIds: payload.memberParticleIds,
      customPrompt: payload.description || undefined,
      model: payload.model,
    });
  }, [session]);

  const handleSubmit1anq = useCallback(async (payload: {
    memberParticleIds: string[];
    description: string;
    directionPreview?: string;
    promptPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => {
    if (!session) return;
    setPanel1anqSubmitting(true);
    setPanel1anqError(null);
    try {
      const result = await ttqWorkbenchService.runPatternUnify(session.id, {
        sourceParticleIds: payload.memberParticleIds,
        customPrompt: payload.description || undefined,
        directionPreview: payload.directionPreview || payload.promptPreview,
        promptPreview: payload.promptPreview || payload.directionPreview,
        model: payload.model,
      });
      const primaryId = payload.memberParticleIds[0] || null;
      if (primaryId) {
        setDerivedFocus({
          sourceParticleId: primaryId,
          pathRunId: result.pathRun?.id,
        });
      }
      const derivedId = result.derivedParticleIds?.[0] || null;
      setPendingCenterParticleId(derivedId);
      setPendingCenterPreferBirdsEye(true);
      // 聚合周期结束：清空多选，避免倒计时结束后底栏「多对一」菜单再弹出
      setBirdsSelectedIds([]);
      if (result.ttqa) {
        setSession(result.ttqa as TtqSession);
      } else {
        void loadSession(true);
      }
      setReadyDialogOpen(true);
      setPanel1anqOpen(false);
      setPanel1anqMemberIds([]);
    } catch (submitError) {
      setPanel1anqError(submitError instanceof Error ? submitError.message : '综合同解生成失败');
    } finally {
      setPanel1anqSubmitting(false);
    }
  }, [loadSession, session, setDerivedFocus]);

  const handlePreviewCttq = useCallback(async (payload: {
    cttqParticleId: string;
    memberParticleIds: string[];
    description: string;
    model?: TtqModelInvocationOptions;
  }) => {
    if (!session) throw new Error('会话未加载');
    return ttqWorkbenchService.previewCttqDirection(session.id, {
      sourceParticleIds: payload.memberParticleIds,
      preferredCttqParticleId: payload.cttqParticleId,
      customPrompt: payload.description || undefined,
      model: payload.model,
    });
  }, [session]);

  const handleSubmitCttq = useCallback(async (payload: {
    cttqParticleId: string;
    memberParticleIds: string[];
    description: string;
    directionPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => {
    if (!session) return;
    setPanelCttqSubmitting(true);
    setPanelCttqError(null);
    setPanelCttqErrorDetail(null);
    try {
      const customParts = [
        payload.description?.trim(),
        payload.directionPreview?.trim()
          ? `【收敛方向】\n${payload.directionPreview.trim()}`
          : '',
      ].filter(Boolean);
      const result = await ttqWorkbenchService.runCttqConverge(session.id, {
        sourceParticleIds: payload.memberParticleIds,
        preferredCttqParticleId: payload.cttqParticleId,
        customPrompt: customParts.join('\n\n') || undefined,
        isPrimary: (session.cttqRecords?.length ?? 0) === 0,
        model: payload.model,
      });
      setDerivedFocus({
        sourceParticleId: payload.cttqParticleId,
        pathRunId: result.pathRun?.id,
      });
      const derivedId = result.derivedParticleIds?.[0] || null;
      setPendingCenterParticleId(derivedId);
      setPendingCenterPreferBirdsEye(true);
      // 聚合周期结束：清空多选，避免倒计时结束后底栏「多对一」菜单再弹出
      setBirdsSelectedIds([]);
      if (result.ttqa) {
        setSession(result.ttqa as TtqSession);
      } else {
        void loadSession(true);
      }
      setReadyDialogOpen(true);
      setPanelCttqOpen(false);
      setPanelCttqMemberIds([]);
    } catch (submitError) {
      const formatted = formatTtqWorkbenchError(submitError);
      setPanelCttqError(formatted.summary);
      setPanelCttqErrorDetail(formatted.detail);
    } finally {
      setPanelCttqSubmitting(false);
    }
  }, [loadSession, session, setDerivedFocus]);

  useEffect(() => {
    // 强制拉全量详情：列表缓存里的 pathRuns 可能缺 source/path，会导致衍生不叠放、聚合失效
    void loadSession(false, { forceRefresh: true });
  }, [loadSession]);

  const hasPipelineRunning = Boolean(session?.particles?.some((p) => (
    p.pipelineStatus === 'pending'
    || p.pipelineStatus === 'pap_running'
    || p.pipelineStatus === 'analyzing'
  )));

  useEffect(() => {
    if (!hasPipelineRunning) return;
    void loadSession(true, { forceRefresh: true });
  }, [hasPipelineRunning]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: only on enter running

  const progress = useMemo(
    () => formatTtqSessionProgress(session?.particles ?? []),
    [session?.particles],
  );

  const tastedAnchorsByParticleId = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (!session) return map;
    for (const entry of session.particles ?? []) {
      const texts = listTastedAnchorTexts(session, entry.particleId);
      if (texts.length > 0) map[entry.particleId] = texts;
    }
    return map;
  }, [session]);

  const refreshChatAnchors = useCallback(async () => {
    const ids = (session?.particles ?? [])
      .map((entry) => String(entry.particleId || '').trim())
      .filter(Boolean);
    if (!ids.length) {
      setChatAnchorsByParticleId({});
      return;
    }
    const unique = [...new Set(ids)];
    const byParticleId = await listAiChatAnchorsBatch(unique);
    const next: Record<string, AiChatSegmentAnchor[]> = {};
    for (const particleId of unique) {
      const anchors = byParticleId[particleId] || [];
      if (anchors.length) next[particleId] = anchors;
    }
    setChatAnchorsByParticleId(next);
  }, [session?.particles]);

  useEffect(() => {
    if (!session?.particles?.length) {
      setChatAnchorsByParticleId({});
      return;
    }
    const timer = window.setTimeout(() => {
      void refreshChatAnchors();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [refreshChatAnchors, session?.particles?.length]);

  useEffect(() => {
    const ids = (session?.particles ?? [])
      .map((entry) => String(entry.particleId || '').trim())
      .filter(Boolean);
    if (!ids.length) return;
    void fetchWorkbenchParticles([...new Set(ids)]);
  }, [session?.id, session?.particles]);

  const handleOpenChatThread = useCallback((threadId: string) => {
    setSegmentPopupOpen(false);
    setAiChatThreadId(threadId);
    setAiChatOpen(true);
  }, []);

  const handleOpenSegmentChat = useCallback(() => {
    setSegmentPopupOpen(false);
    setAiChatThreadId(null);
    setAiChatOpen(true);
  }, []);

  const derivedProvenanceByParticleId = useMemo(() => {
    const map: Record<string, NonNullable<ReturnType<typeof resolveDerivedProvenance>>> = {};
    if (!session) return map;
    for (const entry of session.particles ?? []) {
      const provenance = resolveDerivedProvenance(session, entry.particleId);
      if (provenance) map[entry.particleId] = provenance;
    }
    return map;
  }, [session]);

  const derivedParticleIdSet = useMemo(() => {
    const set = new Set<string>();
    if (!session) return set;
    for (const entry of session.particles ?? []) {
      if (isSessionDerivedParticle(session, entry.particleId)) set.add(entry.particleId);
    }
    return set;
  }, [session]);

  const handleRequestDeleteDerived = useCallback((particleId: string) => {
    if (!session) return;
    if (!isSessionDerivedParticle(session, particleId)) {
      const descendants = listSessionDescendantParticleIds(session, particleId);
      if (descendants.length > 0) {
        setError(ORIGINAL_PARTICLE_HAS_DESCENDANTS_TIP);
        return;
      }
    }
    setPendingDeleteDerivedId(particleId);
  }, [session]);

  const pendingDeleteIsOriginal = useMemo(() => {
    if (!session || !pendingDeleteDerivedId) return false;
    return !isSessionDerivedParticle(session, pendingDeleteDerivedId);
  }, [pendingDeleteDerivedId, session]);

  const pendingDeleteIsCttq = useMemo(() => {
    if (!session || !pendingDeleteDerivedId || pendingDeleteIsOriginal) return false;
    const provenance = resolveDerivedProvenance(session, pendingDeleteDerivedId);
    const angle = String(provenance?.ttqAngleId || provenance?.pathId || '').trim();
    return angle.includes('cttq_converge');
  }, [pendingDeleteDerivedId, pendingDeleteIsOriginal, session]);

  const handleConfirmDeleteDerived = useCallback(async () => {
    if (!session || !pendingDeleteDerivedId) return;
    const particleId = pendingDeleteDerivedId;
    setDeletingDerived(true);
    try {
      const next = await ttqService.removeParticle(session.id, particleId);
      setSession(next);
      setSegmentTray((prev) => prev.filter((item) => item.particleId !== particleId));
      setPendingDeleteDerivedId(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '移除衍生颗粒失败');
    } finally {
      setDeletingDerived(false);
    }
  }, [pendingDeleteDerivedId, session]);

  const pendingRegenProvenance = useMemo(() => {
    if (!session || !pendingRegenParticleId) return null;
    return resolveDerivedProvenance(session, pendingRegenParticleId);
  }, [pendingRegenParticleId, session]);

  const pendingRegenTitle = useMemo(() => {
    if (!session || !pendingRegenParticleId) return null;
    const index = session.particles?.findIndex((item) => item.particleId === pendingRegenParticleId) ?? -1;
    const entry = index >= 0 ? session.particles?.[index] : undefined;
    if (!entry) return null;
    return resolveTtqParticleTitle(entry, index) || null;
  }, [pendingRegenParticleId, session]);

  const handleRegenerateDerived = useCallback(async (
    particleId: string,
    options?: {
      model?: TtqModelInvocationOptions;
      generationPrompt?: string;
      memberParticleIds?: string[];
    },
  ) => {
    if (!session || !ttqaId || regeneratingDerivedId) return;
    const provenance = resolveDerivedProvenance(session, particleId);
    const sourceParticleId = String(provenance?.sourceParticleId || '').trim();
    if (!sourceParticleId) {
      setError('无法定位原始知识颗粒，无法重新生成');
      return;
    }

    const angle = String(provenance?.ttqAngleId || provenance?.pathId || '').trim();
    const variantRaw = String(provenance?.questionChainVariantId || '').trim();
    const isQuestionChain = angle.includes('question_chain') || Boolean(variantRaw);
    const isCttq = angle.includes('cttq_converge');
    const isPatternUnify = angle.includes('pattern_unify');
    const isMultiSolution = angle.includes('multi_solution')
      || (!isQuestionChain && !isCttq && !isPatternUnify && !angle);
    const model = options?.model;
    const generationPrompt = String(options?.generationPrompt || '').trim() || undefined;

    setRegeneratingDerivedId(particleId);
    setError(null);
    try {
      let result;
      if (isQuestionChain) {
        const questionChainVariantId = (variantRaw || 'question_chain') as TtqQuestionChainVariantId;
        result = await ttqWorkbenchService.runQuestionChain(session.id, {
          sourceParticleIds: [sourceParticleId],
          questionChainVariantId,
          batchSize: 1,
          anchorText: provenance?.anchorText,
          customPrompt: provenance?.customPrompt,
          directionPreview: provenance?.directionPreview,
          generationPrompt,
          model,
        });
        setDerivedFocus({
          sourceParticleId,
          pathRunId: result.pathRun?.id,
          questionChainVariantId,
          anchorText: provenance?.anchorText,
        });
      } else if (isCttq) {
        const overrideMembers = (options?.memberParticleIds ?? [])
          .map((id) => String(id || '').trim())
          .filter(Boolean);
        const members = overrideMembers.length >= 2
          ? [...new Set(overrideMembers)]
          : (provenance?.memberParticleIds && provenance.memberParticleIds.length >= 2
            ? provenance.memberParticleIds
            : null);
        if (!members) {
          throw new Error('无法定位收敛成员颗粒，无法重新生成');
        }
        result = await ttqWorkbenchService.runCttqConverge(session.id, {
          sourceParticleIds: members,
          preferredCttqParticleId: sourceParticleId,
          customPrompt: provenance?.customPrompt,
          generationPrompt,
          model,
        });
        setDerivedFocus({
          sourceParticleId,
          pathRunId: result.pathRun?.id,
        });
      } else if (isPatternUnify) {
        const overrideMembers = (options?.memberParticleIds ?? [])
          .map((id) => String(id || '').trim())
          .filter(Boolean);
        const members = overrideMembers.length >= 2
          ? [...new Set(overrideMembers)]
          : (provenance?.memberParticleIds && provenance.memberParticleIds.length >= 2
            ? provenance.memberParticleIds
            : null);
        if (!members) {
          throw new Error('无法定位一解多题成员颗粒，无法重新生成');
        }
        result = await ttqWorkbenchService.runPatternUnify(session.id, {
          sourceParticleIds: members,
          customPrompt: provenance?.customPrompt,
          directionPreview: provenance?.directionPreview,
          promptPreview: provenance?.directionPreview,
          generationPrompt,
          model,
        });
        setDerivedFocus({
          sourceParticleId: members[0]!,
          pathRunId: result.pathRun?.id,
        });
      } else if (isMultiSolution) {
        result = await ttqWorkbenchService.runMultiSolution(session.id, {
          sourceParticleIds: [sourceParticleId],
          batchSize: 1,
          customPrompt: provenance?.customPrompt,
          generationPrompt,
          model,
        });
        setDerivedFocus({ sourceParticleId });
      } else {
        throw new Error('暂不支持该衍生类型的重新生成');
      }

      const derivedId = result.derivedParticleIds?.[0] || null;
      if (result.ttqa) {
        setSession(result.ttqa as TtqSession);
      } else {
        await loadSession(true);
      }

      try {
        const next = await ttqService.removeParticle(session.id, particleId);
        setSession(next);
        setSegmentTray((prev) => prev.filter((item) => item.particleId !== particleId));
      } catch {
        // 新衍生已生成；旧粒移除失败时仍继续，避免中断体验
      }

      setPendingRegenParticleId(null);
      setPendingCttqMemberIds(null);
      setCttqMemberPickTargetId(null);
      setPendingCenterParticleId(derivedId);
      setPendingCenterPreferBirdsEye(isCttq || isPatternUnify);
      setReadyDialogOpen(true);
    } catch (regenError) {
      setError(regenError instanceof Error ? regenError.message : '重新生成失败');
    } finally {
      setRegeneratingDerivedId(null);
    }
  }, [loadSession, regeneratingDerivedId, session, setDerivedFocus, ttqaId]);

  const handlePickCttqMember = useCallback((
    convergeParticleId: string,
    memberParticleId: string,
  ) => {
    if (!session || regeneratingDerivedId) return;
    const provenance = resolveDerivedProvenance(session, convergeParticleId);
    const fromProvenance = provenance?.memberParticleIds ?? [];
    const fromLayout = listTtqBirdsEyeConvergeMemberIds(session, convergeParticleId);
    const existing = fromProvenance.length >= 2 ? fromProvenance : fromLayout;
    if (existing.includes(memberParticleId)) {
      setError('该节点已在当前聚合中');
      setCttqMemberPickTargetId(null);
      return;
    }
    const memberProv = resolveDerivedProvenance(session, memberParticleId);
    const memberAngle = String(memberProv?.ttqAngleId || memberProv?.pathId || '').trim();
    if (memberAngle.includes('cttq_converge')) {
      setError('请选择非收敛代表题的知识颗粒');
      return;
    }
    const nextMembers = [...new Set([...existing, memberParticleId])];
    if (nextMembers.length < 2) {
      setError('聚合至少需要两道成员题');
      return;
    }
    setCttqMemberPickTargetId(null);
    setPendingCttqMemberIds(nextMembers);
    setPendingRegenParticleId(convergeParticleId);
  }, [regeneratingDerivedId, session]);

  const cttqAggregating = useMemo(() => {
    if (!session || !regeneratingDerivedId) return false;
    const provenance = resolveDerivedProvenance(session, regeneratingDerivedId);
    const angle = String(provenance?.ttqAngleId || provenance?.pathId || '').trim();
    return angle.includes('cttq_converge');
  }, [regeneratingDerivedId, session]);

  const openRegenDialog = useCallback((particleId: string) => {
    setPendingCttqMemberIds(null);
    setPendingRegenParticleId(particleId);
  }, []);

  const excludedParticleIds = useMemo(
    () => (session?.particles ?? []).map((e) => e.particleId),
    [session?.particles],
  );

  const hiddenTtqBehaviorIds = useMemo(() => {
    if (birdsEyeMode || !session) return [] as TtqBehaviorId[];
    const selectedId = behaviorSelectedParticleIds[0];
    if (!selectedId) return [] as TtqBehaviorId[];
    if (isMultiSolutionDerivedParticle(session, selectedId)) {
      return ['multi_solution'] as TtqBehaviorId[];
    }
    const provenance = resolveDerivedProvenance(session, selectedId);
    const angle = String(provenance?.ttqAngleId || provenance?.pathId || '').trim();
    if (angle.includes('multi_solution')) {
      return ['multi_solution'] as TtqBehaviorId[];
    }
    return [] as TtqBehaviorId[];
  }, [behaviorSelectedParticleIds, birdsEyeMode, session]);

  const handleAddParticles = async (particleIds: string[]) => {
    if (!session) return;
    const { session: next } = await ttqService.addParticles(session.id, particleIds);
    setSession(next);
    setAddOpen(false);
  };

  useEffect(() => {
    if (!moreMenuOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!moreMenuRef.current?.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [moreMenuOpen]);

  const handleDelete = async () => {
    if (!session) return;
    const hard = canHardDeleteTtqSession(session);
    const ok = window.confirm(
      hard
        ? '该品题尚未添加颗粒或未进行分析，将永久删除。'
        : '该品题将隐藏并保留分析数据，列表中不再显示。',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await ttqService.deleteSession(session.id);
      navigate('/ttq');
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const openMergeDialog = async () => {
    if (!session) return;
    setMergeLoading(true);
    setError(null);
    try {
      const subject = session.subjectCode === 'yuwen' ? 'yuwen' : 'math';
      const rows = await ttqService.listSessions(subject);
      setMergePeerSessions(Array.isArray(rows) ? rows : []);
      setMergeDialogOpen(true);
    } catch (mergeLoadError) {
      setError(mergeLoadError instanceof Error ? mergeLoadError.message : '加载品题列表失败');
    } finally {
      setMergeLoading(false);
    }
  };

  if (loading && !session) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Link to="/ttq" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft size={16} />
          返回品题列表
        </Link>
        <p className="text-danger">{error || '品题会话不存在'}</p>
      </div>
    );
  }

  const particleCount = session.particles?.length ?? 0;
  const nextHint = ttqSessionNextStepHint({
    particleCount,
    doneCount: progress.done,
    runningCount: progress.running,
    failedCount: progress.failed,
    aggregationAvailable: session.aggregationAvailable,
  });

  return (
    <div className="relative h-full min-h-0 overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 px-4 pt-2">
        <div className="pointer-events-auto space-y-1.5 rounded-lg border border-border bg-surface/95 px-3 py-2 shadow-sm backdrop-blur">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/ttq" className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline">
              <ArrowLeft size={14} />
              品题列表
            </Link>
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <h1 className="min-w-0 truncate text-base font-semibold leading-snug text-ink">
                {resolveTtqSessionTitle(session)}
              </h1>
              <button
                type="button"
                className="inline-flex shrink-0 items-center justify-center text-muted hover:text-ink"
                aria-label="编辑品题"
                title="编辑品题"
                onClick={() => setDetailOpen(true)}
              >
                <Pencil size={14} />
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-black/5"
                aria-label="扫描品题答题纸"
                title="扫描品题答题纸"
                onClick={openTtqScan}
              >
                <QrCode size={16} />
              </button>
              <button
                type="button"
                disabled={reloadingSession}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-black/5 disabled:opacity-50"
                aria-label="重新加载"
                title="重新加载"
                onClick={() => { void handleReloadSession(); }}
              >
                {reloadingSession ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
              </button>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-black/5"
                aria-label="添加颗粒"
                title="添加颗粒"
                onClick={() => setAddOpen(true)}
              >
                <Plus size={16} />
              </button>
              <TtqIpttqToolbarButton
                ttqaId={session.id}
                initialRecords={session.ipttqRecords}
                particleCount={session.particles?.length || 0}
                sessionUpdatedAt={session.updatedAt}
                particleAddedAts={(session.particles || []).map((item) => item.addedAt)}
                sessionParticleKey={sessionParticleIdKey(session)}
              />
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-black/5"
                aria-label="重新布局卡片"
                title="重新布局卡片"
                onClick={() => { relayoutCards(); }}
              >
                <LayoutGrid size={16} />
              </button>
              <div ref={moreMenuRef} className="relative">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-black/5"
                  aria-label="更多操作"
                  aria-expanded={moreMenuOpen}
                  title="更多操作"
                  onClick={() => setMoreMenuOpen((open) => !open)}
                >
                  <MoreVertical size={16} />
                </button>
                {moreMenuOpen ? (
                  <div className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[140px] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04]"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        setDetailOpen(true);
                      }}
                    >
                      <Info size={14} />
                      详情
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                      disabled={mergeLoading}
                      onClick={() => {
                        setMoreMenuOpen(false);
                        void openMergeDialog();
                      }}
                    >
                      {mergeLoading ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
                      合并品题到当前
                    </button>
                    <div className="my-1 border-t border-border" role="separator" />
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                      disabled={deleting}
                      onClick={() => {
                        setMoreMenuOpen(false);
                        void handleDelete();
                      }}
                    >
                      <Trash2 size={14} />
                      删除
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex min-w-0 items-center justify-between gap-2">
            <p className="min-w-0 flex-1 truncate text-xs text-muted">
              {titleRefreshMessage || nextHint}
            </p>
            <span className="shrink-0 text-xs text-muted">{progress.label}</span>
          </div>
        </div>
      </div>

      {error ? (
        <p className={`pointer-events-none absolute left-4 right-4 z-30 text-sm text-danger ${TTQ_SESSION_OVERLAY_TOP_CLASS}`}>{error}</p>
      ) : null}

      {particleCount === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center px-4 pt-14">
          <div className="mx-auto w-full max-w-3xl">
            <SectionCard title="品题导图" subtitle="尚未添加知识颗粒" tone="muted">
              <p className="text-sm text-muted">
                点击「添加颗粒」选择知识颗粒。系统将自动提取隐式能力包，并给出品位结论。
              </p>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
                onClick={() => setAddOpen(true)}
              >
                添加第一个颗粒
              </button>
            </SectionCard>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0">
          <TtqUnifiedCanvas
            session={session}
            canvasModel={canvasModel}
            birdsEyeMode={birdsEyeMode}
            viewport={canvasModel?.state.viewport}
            viewportInitialized={canvasModel?.state.viewportInitialized}
            onViewportChange={setViewport}
            onMoveParticle={moveParticle}
            onSwitchLayer={switchLayer}
            onSelectObject={selectObject}
            selectedSegmentObjectIds={segmentTray.map((item) => item.objectId)}
            onBehaviorSelectionChange={handleBehaviorSelectionChange}
            onToggleSegment={handleToggleSegment}
            onOpenPinTi={handleOpenPinTi}
            onOpenLadderPractice={handleOpenLadderPractice}
            showLadderPractice={showLadderPractice}
            onClearSegmentTray={handleClearSegmentTray}
            tastedAnchorsByParticleId={tastedAnchorsByParticleId}
            chatAnchorsByParticleId={chatAnchorsByParticleId}
            onOpenChatThread={handleOpenChatThread}
            derivedProvenanceByParticleId={derivedProvenanceByParticleId}
            derivedParticleIds={[...derivedParticleIdSet]}
            onDeleteDerivedParticle={handleRequestDeleteDerived}
            onRegenerateDerivedParticle={openRegenDialog}
            regeneratingDerivedId={regeneratingDerivedId}
            onMeasureParticle={measureParticle}
            onCenterParticle={centerParticle}
            birdsSelectedIds={birdsSelectedIds}
            onBirdsSelectedChange={setBirdsSelectedIds}
            pinnedFloats={canvasModel?.state.birdsEyePinnedFloats}
            onPinnedFloatsChange={setBirdsEyePinnedFloats}
            cttqAggregating={cttqAggregating}
            cttqMemberPickTargetId={cttqMemberPickTargetId}
            onStartCttqMemberPick={setCttqMemberPickTargetId}
            onCancelCttqMemberPick={() => setCttqMemberPickTargetId(null)}
            onPickCttqMember={handlePickCttqMember}
            focusParticleId={birdsEyeFocusParticleId}
            onFocusParticleHandled={() => setBirdsEyeFocusParticleId(null)}
            onEnterBirdsEyeZoom={enterBirdsEyeZoom}
            onEnterDeriveZoom={enterDeriveZoom}
            convergeRelayoutNonce={convergeRelayoutNonce}
          />
        </div>
      )}

      {pendingDeleteDerivedId ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => {
            if (!deletingDerived) setPendingDeleteDerivedId(null);
          }}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-border bg-surface p-4 shadow-xl"
            role="dialog"
            aria-modal
            aria-labelledby="ttq-delete-derived-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="ttq-delete-derived-title" className="text-sm font-semibold text-ink">
              {pendingDeleteIsOriginal
                ? '移出原始知识颗粒？'
                : pendingDeleteIsCttq
                  ? '移出收敛品题？'
                  : '移出衍生知识颗粒？'}
            </p>
            <p className="mt-1 text-xs text-muted">
              将从本品题会话移除，画布上不再显示。知识颗粒本身不会被删除。
            </p>
            <p className="mt-2 break-all font-mono text-[10px] text-stone-400">
              {pendingDeleteDerivedId}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-lg border border-border py-2 text-sm text-muted hover:bg-black/[0.03] disabled:opacity-50"
                disabled={deletingDerived}
                onClick={() => setPendingDeleteDerivedId(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-danger py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                disabled={deletingDerived}
                onClick={() => { void handleConfirmDeleteDerived(); }}
              >
                {deletingDerived ? '移除中…' : '确认移除'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TtqRegenerateConfirmDialog
        open={Boolean(pendingRegenParticleId)}
        particleId={pendingRegenParticleId}
        particleTitle={pendingRegenTitle}
        provenance={pendingRegenProvenance}
        memberParticleIdsOverride={pendingCttqMemberIds}
        submitting={Boolean(regeneratingDerivedId)}
        onCancel={() => {
          if (regeneratingDerivedId) return;
          setPendingRegenParticleId(null);
          setPendingCttqMemberIds(null);
        }}
        onConfirm={({ model, generationPrompt }) => {
          if (!pendingRegenParticleId) return;
          void handleRegenerateDerived(pendingRegenParticleId, {
            model,
            generationPrompt,
            memberParticleIds: pendingCttqMemberIds ?? undefined,
          });
        }}
      />

      <TtqAddParticleDialog
        open={addOpen}
        sessionId={session.id}
        subject={session.subjectCode}
        excludedParticleIds={excludedParticleIds}
        onClose={() => {
          clearTtqAddParticleDraft(session.id);
          setAddOpen(false);
        }}
        onAdd={handleAddParticles}
      />

      <TtqSessionDetailDialog
        open={detailOpen}
        session={session}
        onClose={() => setDetailOpen(false)}
        onSessionUpdated={(updated) => {
          setSession((current) => {
            if (!current) return updated;
            return { ...current, title: updated.title };
          });
        }}
      />

      <TtqSessionMergeDialog
        open={mergeDialogOpen}
        primarySession={session}
        peerSessions={mergePeerSessions}
        onClose={() => setMergeDialogOpen(false)}
        onMerged={(merged) => {
          setMergeDialogOpen(false);
          setSession(merged);
          navigate(`/ttq/${encodeURIComponent(merged.id)}`, { replace: true });
        }}
      />

      <TtqBottomMenu
        visible={
          !readyDialogOpen
          && !panel1qnaOpen
          && !panel1anqOpen
          && !panelCttqOpen
          && !panel1qnqOpen
          && segmentTray.length === 0
          && (
            birdsEyeMode
              ? birdsSelectedIds.length >= 1
              : behaviorSelectedParticleIds.length >= 1
          )
        }
        menuContext={birdsEyeMode ? 'birds-eye' : 'derive'}
        selectedParticleIds={birdsEyeMode ? birdsSelectedIds : behaviorSelectedParticleIds}
        hiddenBehaviorIds={hiddenTtqBehaviorIds}
        onPrint={openTtqPrintPreview}
        onClear={() => {
          if (birdsEyeMode) setBirdsSelectedIds([]);
          else handleClearBehaviorSelection();
        }}
        onSelectBehavior={handleSelectBehavior}
      />

      <TtqBottomPanel1qnq
        visible={!birdsEyeMode && !readyDialogOpen && panel1qnqOpen}
        items={segmentTray}
        sourceParticleId={panel1qnqSourceId}
        submitting={panel1qnqSubmitting}
        error={panel1qnqError}
        errorDetail={panel1qnqErrorDetail}
        onClear={() => {
          setSegmentTray([]);
          setSegmentPopupOpen(false);
          setSegmentAnchorObjectId(null);
          setAiChatOpen(false);
          setPanel1qnqOpen(false);
          setPanel1qnqSourceId(null);
          setPanel1qnqError(null);
          setPanel1qnqErrorDetail(null);
        }}
        onSubmit={(payload) => { void handleSubmit1qnq(payload); }}
        onPreviewDirection={handlePreview1qnqDirection}
      />

      <TtqSegmentSelectionPopup
        open={
          !readyDialogOpen
          && !panel1qnqOpen
          && !aiChatOpen
          && segmentPopupOpen
          && segmentTray.length > 0
        }
        items={segmentTray}
        anchorObjectId={segmentAnchorObjectId}
        viewportKey={`${canvasModel?.state.viewport?.x ?? 0},${canvasModel?.state.viewport?.y ?? 0},${canvasModel?.state.viewport?.zoom ?? 1}`}
        canvasZoom={canvasModel?.state.viewport?.zoom ?? 1}
        chatOnly={birdsEyeMode}
        onOpenChat={handleOpenSegmentChat}
        onOpenPinTi={handleOpenSegmentPinTi}
        onClear={handleClearSegmentTray}
        onClose={() => {
          setSegmentPopupOpen(false);
          setSegmentAnchorObjectId(null);
        }}
      />

      <AiChatDialogPanel
        open={aiChatOpen && !readyDialogOpen}
        onClose={() => {
          setAiChatOpen(false);
          setAiChatThreadId(null);
          void refreshChatAnchors();
        }}
        particleId={segmentTray[0]?.particleId || panel1qnqSourceId || undefined}
        particleTitle={
          session?.particles?.find(
            (entry) => entry.particleId === (segmentTray[0]?.particleId || panel1qnqSourceId),
          )?.particleTitle || undefined
        }
        contextBlocks={segmentTray.map((item) => ({
          id: item.objectId,
          kind: 'text_segment' as const,
          particleId: item.particleId,
          text: item.text,
        }))}
        sourceParticles={buildAiChatSourceParticles(
          session?.particles,
          segmentTray.map((item) => item.particleId),
        )}
        entrypoint="ttqib-segment"
        surface="ttq_session"
        ttqaId={ttqaId || undefined}
        threadId={aiChatThreadId}
      />

      <TtqBottomPanel1qna
        visible={!birdsEyeMode && !readyDialogOpen && panel1qnaOpen && !panel1qnaAssessOpen}
        sourceParticleId={panel1qnaSourceId}
        submitting={panel1qnaSubmitting}
        error={panel1qnaError}
        errorDetail={panel1qnaErrorDetail}
        onClose={() => {
          setPanel1qnaOpen(false);
          setPanel1qnaSourceId(null);
          setPanel1qnaError(null);
          setPanel1qnaErrorDetail(null);
          setPanel1qnaDescription('');
          setPanel1qnaLlmModel(undefined);
        }}
        onAssess={(payload) => { void handleAssess1qna(payload); }}
      />

      <TtqMultiSolutionAssessModal
        open={panel1qnaAssessOpen}
        assessing={panel1qnaAssessing}
        generating={panel1qnaGenerating}
        assessment={panel1qnaAssessment}
        assessError={panel1qnaAssessError}
        assessErrorDetail={panel1qnaAssessErrorDetail}
        selectedIndexes={panel1qnaSelectedIndexes}
        imageModels={panel1qnaImageModels}
        imageModel={panel1qnaImageModel}
        onImageModelChange={setPanel1qnaImageModel}
        onToggleIndex={handleToggle1qnaCandidate}
        onClose={() => {
          if (panel1qnaGenerating) return;
          setPanel1qnaAssessOpen(false);
          setPanel1qnaAssessment(null);
          setPanel1qnaAssessError(null);
          setPanel1qnaAssessErrorDetail(null);
        }}
        onRetryAssess={() => {
          if (panel1qnaSourceId) void run1qnaAssess(panel1qnaSourceId, panel1qnaLlmModel);
        }}
        onConfirmGenerate={() => { void handleConfirm1qnaGenerate(); }}
      />

      <TtqBottomPanel1anq
        visible={birdsEyeMode && !readyDialogOpen && panel1anqOpen}
        memberParticleIds={panel1anqMemberIds}
        memberLabels={Object.fromEntries(
          (session.particles ?? [])
            .filter((entry) => panel1anqMemberIds.includes(entry.particleId))
            .map((entry, index) => [
              entry.particleId,
              resolveTtqParticleTitle(entry, index) || entry.particleId.slice(0, 8),
            ]),
        )}
        submitting={panel1anqSubmitting}
        error={panel1anqError}
        onClose={() => {
          setPanel1anqOpen(false);
          setPanel1anqMemberIds([]);
          setPanel1anqError(null);
        }}
        onAssess={handleAssess1anq}
        onSubmit={(payload) => { void handleSubmit1anq(payload); }}
      />

      <TtqBottomPanelCttq
        visible={birdsEyeMode && !readyDialogOpen && panelCttqOpen}
        memberParticleIds={panelCttqMemberIds}
        memberLabels={Object.fromEntries(
          (session.particles ?? [])
            .filter((entry) => panelCttqMemberIds.includes(entry.particleId))
            .map((entry, index) => [
              entry.particleId,
              resolveTtqParticleTitle(entry, index) || entry.particleId.slice(0, 8),
            ]),
        )}
        submitting={panelCttqSubmitting}
        error={panelCttqError}
        errorDetail={panelCttqErrorDetail}
        onClose={() => {
          setPanelCttqOpen(false);
          setPanelCttqMemberIds([]);
          setPanelCttqError(null);
          setPanelCttqErrorDetail(null);
        }}
        onPreviewDirection={handlePreviewCttq}
        onSubmit={(payload) => { void handleSubmitCttq(payload); }}
      />

      <Ttq1qnqReadyDialog
        open={readyDialogOpen}
        onClose={handleReadyDialogClose}
      />

      <TtqParticleLadderPracticeModal
        open={Boolean(ladderPracticeParticleId)}
        particleId={ladderPracticeParticleId}
        particleTitle={ladderPracticeTitle}
        subject={session.subjectCode}
        onClose={() => setLadderPracticeParticleId(null)}
        onToast={(message) => setTtqToast(message)}
      />

      <RevisionPrintPreviewModal
        visible={ttqPrintAction.previewVisible}
        particles={ttqPrintParticles}
        resolvingEmbeddedImages={ttqPrintAction.resolvingEmbeddedImages}
        sessionId={ttqaId}
        printSessionId={ttqPrintAction.printSessionId}
        sheetKind="ttqas"
        ttqaId={ttqaId}
        previewTitle="品题答题纸"
        printing={ttqPrintAction.printing}
        sharing={ttqPrintAction.sharing}
        onClose={ttqPrintAction.closePreview}
        onPrint={() => { void ttqPrintAction.print(); }}
        onShare={() => { void ttqPrintAction.share(); }}
      />

      {ttqToast ? (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-border bg-surface px-4 py-2 text-sm text-ink shadow-lg">
          {ttqToast}
        </div>
      ) : null}
    </div>
  );
}
